import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, Play, Pause, Download } from "lucide-react";
import { Lyrics } from "./Lyrics";
import { API, Cover, ShareButton, mmss } from "./SongCard";
import { useMusicPlayer } from "../../context/MusicPlayerContext";

// Full-page lyrics reader: cover, details, full lyrics (follow-along when this song is playing). Esc / X / backdrop close it.
export function LyricsView({ song, onClose }) {
  const q = useMusicPlayer();
  const active = q.isActive(song);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return createPortal(
    <div className="veergi fixed inset-0 z-[100] bg-[#0b0709]/97 backdrop-blur-xl text-[#f3e9d2] overflow-y-auto" role="dialog" aria-modal="true" aria-label={`${song.title} lyrics`} data-testid={`lyrics-view-${song.id}`}>
      <button type="button" onClick={onClose} aria-label="Close lyrics" data-testid="lyrics-view-close"
        className="fixed top-4 right-4 z-10 h-11 w-11 rounded-full bg-[#f3e9d2]/10 hover:bg-[#e9c46a] hover:text-[#120a0c] grid place-items-center transition-colors">
        <X size={20} />
      </button>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-10 lg:gap-16">
        <div className="lg:sticky lg:top-20 self-start">
          <Cover song={song} className="w-full aspect-square rounded-sm shadow-[0_40px_120px_-30px_rgba(233,196,106,0.35)]" />
          <p className="mt-6 font-display text-[#e9c46a] text-xs tracking-[0.3em]">{song.album_title ? song.album_title.toUpperCase() : "SINGLE"}{song.track_no ? ` · TRACK ${String(song.track_no).padStart(2, "0")}` : ""}</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl leading-[0.95]" data-testid="lyrics-view-title">{song.title}</h1>
          <p className="mt-3 text-sm uppercase tracking-[0.2em] text-[#f3e9d2]/60" data-testid="lyrics-view-artist">
            {song.artist}{song.duration_sec ? ` · ${mmss(song.duration_sec)}` : ""}{song.year ? ` · ${song.year}` : ""}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => q.playSong(song)} data-testid="lyrics-view-play"
              className="inline-flex items-center gap-2 rounded-full bg-[#e9c46a] text-[#120a0c] text-sm font-bold px-5 py-2.5 hover:scale-[1.03] transition-transform">
              {active && q.playing ? <Pause size={15} /> : <Play size={15} />} {active && q.playing ? "Pause" : "Play"}
            </button>
            <a href={`${API}/music/${song.id}/download`} download data-testid="lyrics-view-download"
              className="inline-flex items-center gap-2 rounded-full border border-[#f3e9d2]/25 text-sm font-semibold px-4 py-2 hover:border-[#e9c46a] hover:text-[#e9c46a] transition-colors">
              <Download size={14} /> Download
            </a>
            <ShareButton song={song} />
          </div>
          <Link to={`/music/${song.slug || song.id}`} onClick={onClose} className="mt-5 inline-block text-xs tracking-[0.2em] uppercase text-[#f3e9d2]/50 hover:text-[#e9c46a]" data-testid="lyrics-view-song-page">Open song page →</Link>
          {song.copyright && <p className="mt-6 text-[11px] text-[#f3e9d2]/40" data-testid="lyrics-view-copyright">{song.copyright}</p>}
        </div>
        <div>
          <p className="font-display text-[#e9c46a] text-xs tracking-[0.3em] mb-6">LYRICS</p>
          {song.lyrics
            ? <Lyrics text={song.lyrics} time={active ? q.time : 0} active={active} onSeek={(t) => { if (!active) q.playSong(song); q.seek(t); }} className="text-lg sm:text-xl leading-9 text-[#f3e9d2]/85" />
            : <p className="text-[#f3e9d2]/50 italic" data-testid="lyrics-view-empty">Lyrics for this track haven't been added yet.</p>}
        </div>
      </div>
    </div>,
    document.body
  );
}
